import appConfig from '@/features/app/constants/app-config';
import captureSentryException from '@/features/app/functions/captureSentryException';
import useUnregisterPushDeviceMutation from '@/features/push/mutations/useUnregisterPushDeviceMutation';
import { useAppDispatch } from '@/features/app/hooks/useAppDispatch';
import useCanClientUseCookieTokenStorage from '@/features/auth/hooks/useCanClientUseCookieTokenStorage';
import reduxAuthStateSlice from '@/features/auth/stores/redux-auth-state-slice';
import { ReactNativeIpcMessage } from '@/features/mobile-app-bridge/constants/message-action-schema';
import sendIpcMessage from '@/features/mobile-app-bridge/functions/send-ipc-message';
import useAxios from '@/shared/hooks/useAxios';
import { useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { useCallback } from 'react';
import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';

export default function useLogoutUser() {
  const featureFlags = useFeatureFlags();
  const axios = useAxios();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const canClientUseCookieStorage = useCanClientUseCookieTokenStorage();
  const { mutateAsync: unregisterPushDevice } = useUnregisterPushDeviceMutation();

  return useCallback(
    async (beforeDispatch?: () => void, callLogoutApi = true) => {
      try {
        await unregisterPushDevice();
      } catch (error) {
        captureSentryException(error, {
          tags: { feature: 'push.logout' },
        });
      }

      let promise;
      if (featureFlags?.auth?.useCookieTokenStorage) {
        if (callLogoutApi) {
          promise = axios.post('/api/v1/logout');
        } else {
          promise = Promise.resolve();
        }
        promise = promise
          .then(async () => {
            if (window.ReactNativeWebView) {
              return sendIpcMessage(
                {
                  action: ReactNativeIpcMessage.enum.LOGOUT_USER,
                },
                3000
              ).catch((e) => {
                captureSentryException(e, {
                  message: 'Failed to remove user credentials from mobile app storage',
                  tags: {
                    feature: 'auth.logout',
                  },
                });
              });
            }
          })
          .finally(() => {
            Cookies.remove(appConfig.accessTokenExpireDateKey);
            Cookies.remove(appConfig.refreshTokenExpireDateKey);
            Cookies.remove(appConfig.rememberMeKey);
          });
      }
      if (!canClientUseCookieStorage && featureFlags?.auth?.useLegacyTokenStorage) {
        Cookies.remove(appConfig.authTokenKey);
        if (window.ReactNativeWebView) {
          promise = sendIpcMessage(
            {
              action: ReactNativeIpcMessage.enum.LOGOUT_USER,
            },
            3000
          ).catch((e) => {
            captureSentryException(e, {
              message: 'Failed to remove user credentials from mobile app storage',
              tags: {
                feature: 'auth.logout',
              },
            });
          });
        } else {
          promise = Promise.resolve();
        }
      }

      if (!promise) {
        promise = Promise.resolve();
      }

      return promise
        .then(() => {
          beforeDispatch?.();
          dispatch(
            reduxAuthStateSlice.actions.logoutUser({
              autoLogout: false,
            })
          );
        })
        .catch((e) => {
          captureSentryException(e, {
            tags: {
              feature: 'auth.logout',
            },
          });
          beforeDispatch?.();
          dispatch(
            reduxAuthStateSlice.actions.logoutUser({
              autoLogout: false,
            })
          );
        })
        .finally(() => {
          // clear all cached queries except ['adminSetting']
          queryClient
            .getQueryCache()
            .findAll()
            .filter((query) => query.queryKey[0] !== 'adminSetting')
            .forEach((cache) => {
              queryClient.removeQueries({ queryKey: cache.queryKey });
            });
        });
    },
    [
      featureFlags?.auth?.useCookieTokenStorage,
      featureFlags?.auth?.useLegacyTokenStorage,
      canClientUseCookieStorage,
      axios,
      dispatch,
      queryClient,
      unregisterPushDevice,
    ]
  );
}
